import React, { useState, useEffect, useMemo } from 'react';
import { Box, TextField, InputAdornment, Typography, Chip } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSignalRContext } from '../../../SignalR/useSignalR';
import MetricsHeader from './MetricsHeader';
import MetricsTable from './MetricsTable';
import { calculateAverageMetrics } from './utils';

/**
 * ExecutionMetrics Component
 * Main container for execution metrics display
 * Fully isolated - no impact on existing components
 */
const ExecutionMetrics = () => {
    const theme = useTheme();
    const { executionMetricsList } = useSignalRContext();

    const [searchTerm, setSearchTerm] = useState('');
    const [filterQuality, setFilterQuality] = useState('all'); // all, excellent, good, poor

    // Calculate metrics for header
    const metrics = useMemo(() => {
        return calculateAverageMetrics(executionMetricsList || []);
    }, [executionMetricsList]);

    // Filter executions based on search and quality filter
    const filteredExecutions = useMemo(() => {
        let filtered = executionMetricsList || [];

        // Search filter
        if (searchTerm && searchTerm.trim() !== '') {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(ex =>
                ex.symbolName?.toLowerCase().includes(search) ||
                ex.lpName?.toLowerCase().includes(search) ||
                ex.orderId?.toString().includes(search) ||
                ex.clOrdId?.toLowerCase().includes(search)
            );
        }

        // Quality filter
        if (filterQuality !== 'all') {
            filtered = filtered.filter(ex => {
                const latency = ex.submissionToFillLatencyMs || 0;
                const slippage = Math.abs(ex.slippagePips || 0);

                if (filterQuality === 'excellent') {
                    return latency < 200 && slippage < 0.5;
                } else if (filterQuality === 'good') {
                    return latency >= 200 && latency < 500 && slippage < 1.0;
                } else if (filterQuality === 'poor') {
                    return latency >= 1000 || slippage >= 2.0;
                }
                return true;
            });
        }

        // Sort by time (newest first)
        return filtered.sort((a, b) => {
            const timeA = new Date(a.time).getTime();
            const timeB = new Date(b.time).getTime();
            return timeB - timeA;
        });
    }, [executionMetricsList, searchTerm, filterQuality]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default
            }}
        >
            {/* Header with Performance Dashboard */}
            <MetricsHeader metrics={metrics} />

            {/* Filters and Search */}
            <Box
                sx={{
                    px: { xs: 1, sm: 2 },
                    pb: 1.5,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}
            >
                {/* Search Bar */}
                <TextField
                    size="small"
                    placeholder="Search by symbol, LP, or Order ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'rgba(140, 200, 240, 1)' }} />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        minWidth: { xs: '100%', sm: 300 },
                        backgroundColor: theme.palette.background.paper
                    }}
                />

                {/* Quality Filters */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            textTransform: 'uppercase'
                        }}
                    >
                        Quality:
                    </Typography>
                    <Chip
                        label="All"
                        size="small"
                        onClick={() => setFilterQuality('all')}
                        sx={{
                            backgroundColor: filterQuality === 'all'
                                ? theme.palette.primary.main
                                : theme.palette.background.paper,
                            color: filterQuality === 'all'
                                ? '#ffffff'
                                : theme.palette.text.primary,
                            fontWeight: 600,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: filterQuality === 'all'
                                    ? theme.palette.primary.dark
                                    : theme.palette.action.hover
                            }
                        }}
                    />
                    <Chip
                        label="Excellent"
                        size="small"
                        onClick={() => setFilterQuality('excellent')}
                        icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#10b981', ml: 0.5 }} />}
                        sx={{
                            backgroundColor: filterQuality === 'excellent'
                                ? '#10b981'
                                : theme.palette.background.paper,
                            color: filterQuality === 'excellent'
                                ? '#ffffff'
                                : theme.palette.text.primary,
                            fontWeight: 600,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: filterQuality === 'excellent'
                                    ? '#059669'
                                    : theme.palette.action.hover
                            }
                        }}
                    />
                    <Chip
                        label="Good"
                        size="small"
                        onClick={() => setFilterQuality('good')}
                        icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b', ml: 0.5 }} />}
                        sx={{
                            backgroundColor: filterQuality === 'good'
                                ? '#f59e0b'
                                : theme.palette.background.paper,
                            color: filterQuality === 'good'
                                ? '#ffffff'
                                : theme.palette.text.primary,
                            fontWeight: 600,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: filterQuality === 'good'
                                    ? '#d97706'
                                    : theme.palette.action.hover
                            }
                        }}
                    />
                    <Chip
                        label="Poor"
                        size="small"
                        onClick={() => setFilterQuality('poor')}
                        icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444', ml: 0.5 }} />}
                        sx={{
                            backgroundColor: filterQuality === 'poor'
                                ? '#ef4444'
                                : theme.palette.background.paper,
                            color: filterQuality === 'poor'
                                ? '#ffffff'
                                : theme.palette.text.primary,
                            fontWeight: 600,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: filterQuality === 'poor'
                                    ? '#dc2626'
                                    : theme.palette.action.hover
                            }
                        }}
                    />
                </Box>
            </Box>

            {/* Execution Metrics Table */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    px: { xs: 1, sm: 2 },
                    pb: 2,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <MetricsTable executions={filteredExecutions} />
            </Box>
        </Box>
    );
};

export default ExecutionMetrics;
